import { ActivatedRouteSnapshot } from '@angular/router';

/**
 * The project sections that a customer has to activate before they can be
 * used, keyed by the route path — which is also the product slug the Payment
 * API reports in `activatedProducts` (see `productActivationGuard`).
 *
 * `settings` is deliberately absent: the Manage area hangs off no product, so
 * it travels with an app switch unconditionally.
 */
const PRODUCT_SECTIONS: readonly string[] = [
  'voice',
  'rooms',
  'fleet',
  'cortex',
];

/** Whether a section is one a project has to have activated. */
export function isProductSection(section: string | undefined): boolean {
  return !!section && PRODUCT_SECTIONS.includes(section);
}

/**
 * The part of the current URL that survives a move to another project.
 *
 * Switching projects in the header used to drop the user on the project's
 * default page, so someone reading Fleet → Images landed on Fleet → Analytics
 * every time. Keeping their place means replaying the path under the *new*
 * `/projects/:projectId` — but only the part of it that means the same thing
 * over there.
 *
 * That line falls at the first **route parameter**. Everything before it names
 * a place (`fleet`, `binary`, `server`); the parameter names a *thing*, and
 * server 820 of one project is nothing in another. So `/fleet/server/820/info`
 * travels as `/fleet/server` — the list the detail page came from.
 *
 * The cut is structural rather than a list of known detail routes: every
 * `ActivatedRouteSnapshot` carries the `routeConfig.path` it matched, and a
 * parameter is exactly a part of that path beginning with `:`. Lining those
 * parts up with the matched URL segments answers the question without anything
 * to keep in sync — a new detail route is handled the day it is added.
 *
 * @param root Snapshot root, i.e. `router.routerState.snapshot.root`.
 * @returns Path segments below `/projects/:projectId`, empty when the current
 *   route is not inside a project at all (the project list, user settings,
 *   sign-in) — the caller then simply lands on the project's default page.
 */
export function projectSectionPath(root: ActivatedRouteSnapshot): string[] {
  let projectRoute: ActivatedRouteSnapshot | null = root;

  while (projectRoute && !routePath(projectRoute).includes(':projectId')) {
    projectRoute = projectRoute.firstChild;
  }

  if (!projectRoute) return [];

  const segments: string[] = [];

  // Start below the project route: its own `projects/:projectId` is the prefix
  // the caller rebuilds, not part of what travels.
  for (
    let node: ActivatedRouteSnapshot | null = projectRoute.firstChild;
    node;
    node = node.firstChild
  ) {
    // A route declared with an empty path (Fleet's shell, for one) matches no
    // URL of its own, so this contributes nothing and walks on to its child.
    const parts = routePath(node).split('/').filter(Boolean);

    for (let i = 0; i < node.url.length; i++) {
      if (parts[i]?.startsWith(':')) return withoutCreateMask(segments);
      segments.push(node.url[i].path);
    }
  }

  return withoutCreateMask(segments);
}

/**
 * Drop a trailing `create`, so the switch lands on the list rather than on the
 * new project's empty creation form.
 *
 * `binary/create` and `configs/create` are declared as one route path with a
 * slash, so both halves are literal segments and the parameter rule above does
 * not catch them. Nobody switches projects in order to start creating
 * something in the other one.
 */
function withoutCreateMask(segments: string[]): string[] {
  return segments.at(-1) === 'create' ? segments.slice(0, -1) : segments;
}

function routePath(snapshot: ActivatedRouteSnapshot): string {
  return snapshot.routeConfig?.path ?? '';
}
